import React from "react";

import { Box, InputLabel, TextField } from "@mui/material";

import { DATE_FORMAT_ISO, TIME_FORMAT_HH_MM } from "@/utils/date-utils";

import dayjs from "dayjs";

type InputType = "date" | "datetime" | "year" | "month" | "time";

export interface DatePickerProps {
  label?: string;
  error?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  name?: string;
  required?: boolean;
  min?: string;
  max?: string;
  type: InputType;
  disabled?: boolean;
}

function determineFormat(inputType: string) {
  switch (inputType) {
    case "year":
      return "YYYY";
    case "month":
      return "YYYY-MM";
    case "datetime":
      return `${DATE_FORMAT_ISO} ${TIME_FORMAT_HH_MM}`;
    case "time":
      return TIME_FORMAT_HH_MM;
    case "date":
    default:
      return DATE_FORMAT_ISO;
  }
}

export const DatePicker: React.FC<DatePickerProps> = props => {
  const {
    label = "",
    error = "",
    value: _initValue,
    onChange,
    onBlur,
    name,
    required = false,
    min,
    disabled = false,
  } = props;

  const computedMax = React.useMemo(() => {
    if ((name === "dob" || name === "dateOfBirth") && props.type === "date") {
      return dayjs().format(DATE_FORMAT_ISO);
    }
    return props.max;
  }, [name, props.max, props.type]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    let { value } = e.target;
    if (!value) {
      return onChange?.(null);
    }
    if (props.type === "datetime") {
      value = `${value}:00Z`;
    }
    return onChange?.(value);
  };

  const { dateFormat, inputType, value } = React.useMemo(() => {
    const format = determineFormat(props.type);
    let inputType: string = props.type;
    let value = _initValue;

    if (inputType === "datetime") {
      inputType = "datetime-local";
      if (_initValue) {
        value = dayjs(_initValue).utc().format(format);
      }
    }

    return {
      dateFormat: format,
      inputType,
      value,
    };
  }, [props.type, _initValue]);

  return (
    <Box sx={{ width: "inherit" }}>
      {label && (
        <InputLabel required={required} sx={{ mb: 0.5 }}>
          {label}
        </InputLabel>
      )}
      <TextField
        disabled={disabled}
        fullWidth
        error={Boolean(error)}
        helperText={error ? error : null}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={dateFormat}
        name={name}
        value={value ?? ""}
        type={inputType}
        slotProps={{
          htmlInput: {
            min,
            max: computedMax,
          },
        }}
        sx={{ minHeight: 47 }}
      />
    </Box>
  );
};
